import { actionCreator, ActionReturnType, Loading, loadingNotStarted, loadingIsLoading, loadingFinished, createGuid, loadingFailed } from "@itk/typescript-utils";
import { LoginState, Token } from "./authorization-gate-types";
import { ErrorCodes, Model_ContactPersonPut } from "src/backend/openapi/v1";
import { LoginFlowState } from "./components/login-flow/login-flow-types";

export enum Action {
  ValidateUserRequest = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:VALIDATEUSERREQUEST",
  ValidateUserSuccess = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:VALIDATEUSERSUCCESS",
  PostValidateRequestFail = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:POSTVALIDATEREQUESTFAIL",
  ValidateOtpRequest = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:VALIDATEOTPREQUEST",
  ValidateOtpSuccess = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:VALIDATEOTPSUCCESS",
  ValidateOtpFail = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:VALIDATEOTPFAIL",
  ChangePasswordRequest = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:CHANGEPASSWORDREQUEST",
  ChangePasswordSuccess = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:CHANGEPASSWORDSUCCESS",
  SetPasswordRequest = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:SETPASSWORDREQUEST",
  SetPasswordSuccess = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:SETPASSWORDSUCCESS",

  GetLoginRequest = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:GETLOGINREQUEST",
  GetLoginSuccess = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:GETLOGINSUCCESS",
  GetLoginFail = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:GETLOGINFAIL",

  RenewAccessTokenRequest = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:RENEWACCESSTOKENREQUEST",
  RenewAccessTokenSuccess = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:RENEWACCESSTOKENSUCCESS",

  SetLoginState = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:SETLOGINSTATE",
  SetLoginFlowState = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:SETLOGINFLOWSTATE",
  SetForgotPassword = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:SETFORGOTPASSWORD",

  SetRememberMe = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:SETREMEMBERME",

  SetEmail = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:SETEMAIL",

  AlternativeLoginRequest = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:ALTERNATIVELOGINREQUEST",
  AlternativeLoginSuccess = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:ALTERNATIVELOGINSUCCESS",
  AlternativeLoginFail = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:ALTERNATIVELOGINFAIL",

  LogoutRequest = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:LOGOUTREQUEST",
  LogoutSuccess = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:LOGOUTSUCCESS",

  SendLoginRequestViaEmail = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:SENDLOGINREQUESTVIAEMAIL",

  UpdateUserProfileRequest = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:UPDATEUSERPROFILEREQUEST",

  Clear = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:CLEAR",
  SetIsSubmitting = "@@ITK:SRC:COMPONENTS:AUTHORIZATION-GATE:AUTHORIZATION-GATE-STORE.TS:ACTION:SETISSUBMITTING"
}

type AuthorizationGateState = {
  authorizationGateState: Loading<Token>;
  loginState: LoginState;
  loginFlowState: LoginFlowState;
  loginUsername: string | undefined;
  rememberMe: boolean;
  requestId: string;
  fullName: string | undefined;
  email: string | undefined;
  loginOverrideId: string | undefined;
  isSubmitting: boolean;
};

const initialState: () => AuthorizationGateState = () => ({
  authorizationGateState: loadingNotStarted,
  loginState: { currentState: "loading" },
  loginFlowState: { current: "login" },
  loginUsername: undefined,
  rememberMe: false,
  requestId: createGuid(),
  fullName: undefined,
  email: undefined,
  loginOverrideId: undefined,
  isSubmitting: false
});

export const authorizationGateActions = {
  validateUserRequest: (username: string, requestId: string, isReset: boolean) => actionCreator(Action.ValidateUserRequest, { username, requestId, isReset }),
  validateUserSuccess: (loginFlowState: LoginFlowState) => actionCreator(Action.ValidateUserSuccess, { loginFlowState }),

  validateOtpRequest: (requestId: string, passcode: string, callback?: (success: boolean) => void) => actionCreator(Action.ValidateOtpRequest, { requestId, passcode, callback }),
  validateOtpSuccess: (loginFlowState: LoginFlowState) => actionCreator(Action.ValidateOtpSuccess, { loginFlowState }),
  validateOtpFail: () => actionCreator(Action.ValidateOtpFail),
  postValidateRequestFail: () => actionCreator(Action.PostValidateRequestFail),

  changePasswordRequest: (username: string, newPassword: string, loginFlowState?: LoginFlowState) => actionCreator(Action.ChangePasswordRequest, { username, newPassword, loginFlowState }),
  changePasswordSuccess: () => actionCreator(Action.ChangePasswordSuccess),
  setPasswordRequest: (username: string, newPassword: string, loginFlowState?: LoginFlowState) => actionCreator(Action.SetPasswordRequest, { username, newPassword, loginFlowState }),
  setPasswordSuccess: (username: string, password: string) => actionCreator(Action.SetPasswordSuccess, { username, password }),

  getLoginRequest: (username: string, password: string) => actionCreator(Action.GetLoginRequest, { username, password }),
  getLoginSuccess: (token: Token) => actionCreator(Action.GetLoginSuccess, { token }),
  getLoginFail: () => actionCreator(Action.GetLoginFail),

  renewAccessTokenRequest: (refreshToken: string) => actionCreator(Action.RenewAccessTokenRequest, { refreshToken }),
  renewAccessTokenSuccess: (token: Token) => actionCreator(Action.RenewAccessTokenSuccess, { token }),

  setLoginState: (loginState: LoginState) => actionCreator(Action.SetLoginState, { loginState }),

  setRememberMe: (rememberMe: boolean) => actionCreator(Action.SetRememberMe, { rememberMe }),
  setForgotPassword: () => actionCreator(Action.SetForgotPassword),

  setEmail: (email: string | undefined) => actionCreator(Action.SetEmail, { email }),

  alternativeLoginRequest: (id: string) => actionCreator(Action.AlternativeLoginRequest, { id }),
  alternativeLoginSuccess: (token: Token) => actionCreator(Action.AlternativeLoginSuccess, { token }),

  logoutRequest: (reload = true) => actionCreator(Action.LogoutRequest, { reload }),
  logoutSuccess: () => actionCreator(Action.LogoutSuccess),
  setLoginFlowState: (loginFlowState: LoginFlowState) => actionCreator(Action.SetLoginFlowState, { loginFlowState }),
  clear: () => actionCreator(Action.Clear),

  sendLoginRequestViaEmail: (email: string, requestId: string) => actionCreator(Action.SendLoginRequestViaEmail, { email, requestId }),
  updateUserProfileRequest: (profileDetails: Model_ContactPersonPut) => actionCreator(Action.UpdateUserProfileRequest, { profileDetails }),
  setIsSubmitting: (isSubmitting: boolean) => actionCreator(Action.SetIsSubmitting, { isSubmitting })
};

export const authorizationGateReducer = (state = initialState(), action: ActionReturnType<typeof authorizationGateActions>): AuthorizationGateState => {
  switch (action.type) {
    case Action.ValidateUserRequest: {
      return { ...state, loginUsername: action.payload.username, email: action.payload.username, loginState: { currentState: "loading" }, requestId: action.payload.requestId };
    }
    case Action.ValidateUserSuccess: {
      return { ...state, loginFlowState: action.payload.loginFlowState, loginState: { currentState: "should login" } };
    }

    case Action.ValidateOtpRequest: {
      return { ...state, loginState: { currentState: "loading" } };
    }
    case Action.ValidateOtpSuccess: {
      return { ...state, loginFlowState: action.payload.loginFlowState, loginState: { currentState: "should login" } };
    }
    case Action.ValidateOtpFail: {
      return { ...state, loginState: { currentState: "should login" } };
    }

    case Action.PostValidateRequestFail:
      return { ...state, loginFlowState: { current: "forgot-password", stage: "email" }, loginState: { currentState: "error", errorCode: ErrorCodes.Mismatch } };

    case Action.ChangePasswordRequest: {
      return { ...state, loginState: { currentState: "loading" } };
    }
    case Action.ChangePasswordSuccess: {
      return { ...state, authorizationGateState: loadingNotStarted, loginFlowState: { current: "password" }, loginState: { currentState: "loading" } };
    }
    case Action.SetPasswordRequest: {
      return { ...state, loginState: { currentState: "loading" }, loginFlowState: { current: "change-password" } };
    }

    case Action.GetLoginRequest:
      return { ...state, loginState: { currentState: "loading" }, email: action.payload.username };
    case Action.GetLoginSuccess: {
      return { ...state, authorizationGateState: loadingFinished(action.payload.token), loginState: { currentState: "should login" }, loginFlowState: { current: "email" } };
    }
    case Action.GetLoginFail: {
      return { ...state, authorizationGateState: loadingFailed };
    }

    case Action.RenewAccessTokenRequest:
      return { ...state, authorizationGateState: loadingIsLoading };

    case Action.RenewAccessTokenSuccess:
      return { ...state, authorizationGateState: loadingFinished(action.payload.token) };

    case Action.SetLoginState:
      return { ...state, loginState: action.payload.loginState };
    case Action.SetForgotPassword:
      return { ...state, loginFlowState: { current: "forgot-password", stage: "email" } };

    case Action.SetLoginFlowState:
      return { ...state, loginFlowState: action.payload.loginFlowState };

    case Action.SetRememberMe:
      return { ...state, rememberMe: action.payload.rememberMe };

    case Action.SetEmail:
      return { ...state, email: action.payload.email };

    case Action.AlternativeLoginRequest:
      return { ...state, authorizationGateState: loadingIsLoading, loginOverrideId: action.payload.id };
    case Action.AlternativeLoginSuccess:
      return { ...state, authorizationGateState: loadingFinished(action.payload.token), loginState: { currentState: "should login" } };

    case Action.SendLoginRequestViaEmail:
      return { ...state, requestId: action.payload.requestId };
    case Action.SetIsSubmitting:
      return { ...state, isSubmitting: action.payload.isSubmitting };
    case Action.Clear:
      return initialState();
    default:
      return state;
  }
};
